import './App.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { auth } from './config/firebase';
import Login from './components/auth/Login';
import Signup from './components/auth/Signup';
import ForgotPassword from './components/auth/ForgotPassword';
import ResetPassword from './components/auth/ResetPassword';
import Dashboard from './components/dashboard/Dashboard';
import Profile from './components/profile/Profile';
import CreateGroup from './components/groups/CreateGroup';
import GroupView from './components/groups/GroupView';
import { ToastProvider } from './contexts/ToastContext';
import EmailVerifiedPage from './components/auth/EmailVerifiedPage';

// Protected Route Component - FIXED: Now properly blocks unverified users
const ProtectedRoute = ({ children }) => {
  const [loading, setLoading] = useState(true);
  const [authenticated, setAuthenticated] = useState(false);
  const [verified, setVerified] = useState(false);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        setAuthenticated(true);
        setVerified(user.emailVerified);
        console.log("🔐 Auth Check - User:", user.email, "Verified:", user.emailVerified);
      } else {
        setAuthenticated(false);
        setVerified(false);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen">Loading...</div>;
  }

  if (!authenticated) {
    console.log("❌ Redirecting to login - not authenticated");
    return <Navigate to="/login" />;
  }

  if (!verified) {
    console.log("❌ Redirecting to login - email not verified");
    // Sign out unverified users to prevent them from staying logged in
    auth.signOut();
    return <Navigate to="/login" state={{ error: "Please verify your email before accessing the app." }} />;
  }

  console.log("✅ Access granted - user authenticated and verified");
  return children;
};

// Public Route Component - FIXED: Now checks verification status too
const PublicRoute = ({ children }) => {
  const [loading, setLoading] = useState(true);
  const [authenticated, setAuthenticated] = useState(false);
  const [verified, setVerified] = useState(false);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        setAuthenticated(true);
        setVerified(user.emailVerified);
      } else {
        setAuthenticated(false);
        setVerified(false);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen">Loading...</div>;
  }

  // Only redirect to dashboard if user is both authenticated AND verified
  if (authenticated && verified) {
    return <Navigate to="/dashboard" />;
  }

  return children;
};

function App() {
  return (
    <ToastProvider>
      <BrowserRouter>
        <div className="App">
          <Routes>
            {/* Public Routes */}
            <Route 
              path="/login" 
              element={
                <PublicRoute>
                  <Login />
                </PublicRoute>
              } 
            />
            <Route 
              path="/signup" 
              element={
                <PublicRoute>
                  <Signup />
                </PublicRoute>
              } 
            />
            <Route path="/" element={<Navigate to="/login" />} />

            <Route 
              path="/forgot-password" 
              element={
                <PublicRoute>
                  <ForgotPassword />
                </PublicRoute>
              } 
            />
            
            <Route 
              path="/reset-password" 
              element={
                <PublicRoute>
                  <ResetPassword />
                </PublicRoute>
              } 
            />

            {/* CRITICAL: Email verification should be accessible without authentication */}
            <Route 
              path="/email-verified"
              element={<EmailVerifiedPage />}
            />

            {/* Protected Routes - Now properly secured */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <Profile />
                </ProtectedRoute>
              }
            />
            <Route
              path="/groups/create"
              element={
                <ProtectedRoute>
                  <CreateGroup />
                </ProtectedRoute>
              }
            />
            <Route 
              path="/groups/:groupId" 
              element={
                <ProtectedRoute>
                  <GroupView />
                </ProtectedRoute>
              }
            />
          </Routes>
        </div>
      </BrowserRouter>
    </ToastProvider>
  );
}

export default App;
